import { useEffect, useReducer, useRef } from 'react'
import { useUser, useListBoards, useListSails, useLang } from '../utils/hooks'
import { langRigsStats } from '../assets/langRigsStats.js'
import { cloneDeep, sortBy, filter, find } from 'lodash'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'

import {
  boardStatsNumberOfItemsPerPage,
  sailStatsNumberOfItemsPerPage,
  textClass,
} from '../assets/colors'

import PageSelect from '../components/PageSelect'
import BoardStatList from '../components/BoardStatList'
import SailStatList from '../components/SailStatList'
import Footer from '../components/Footer'

const RigsStats = () => {
  const SORTS = {
    NONE: (list) => list,
    ACTIVE_SORT: (list) => sortBy(list, 'active'),
    BOARD_SORT: (list) => sortBy(list, 'board'),
    SAIL_SORT: (list) => sortBy(list, 'sail'),
    NB_SORT: (list) => sortBy(list, 'nb'),
    DURATION_SORT: (list) => sortBy(list, 'duration'),
    FIRST_USE_SORT: (list) => sortBy(list, 'firstUse'),
    LAST_USE_SORT: (list) => sortBy(list, 'lastUse'),
  }

  const firstDiv = useRef(null)

  const { user } = useUser()
  const { listBoards } = useListBoards()
  const { listSails } = useListSails()
  const { lang } = useLang()
  const choosenLang = lang === 'fr' ? langRigsStats.fr : langRigsStats.eng

  let navigateTo = useNavigate()

  const boardStatsReducer = (state, action) => {
    switch (action.type) {
      case 'NEW_PAGE':
        return {
          ...state,
          listBoardStatsPage: cloneDeep(
            state.data.slice(
              (action.payload - 1) * boardStatsNumberOfItemsPerPage,
              action.payload * boardStatsNumberOfItemsPerPage,
            ),
          ),
          selectedPage: action.payload,
        }
      case 'NEW_ITEM_SELECTED':
        return {
          ...state,
          data: cloneDeep(action.payload),
          listBoardStatsPage: cloneDeep(
            action.payload.slice(
              (state.selectedPage - 1) * boardStatsNumberOfItemsPerPage,
              state.selectedPage * boardStatsNumberOfItemsPerPage,
            ),
          ),
          totalItems: action.payload.length,
        }
      case 'STATS_FETCH_OK':
        return {
          ...state,
          initialData: cloneDeep(action.payload),
          data: cloneDeep(action.payload),
          listBoardStatsPage: cloneDeep(
            action.payload.slice(0, boardStatsNumberOfItemsPerPage),
          ),
          isLoading: false,
          isError: false,
          totalItems: action.payload.length,
          sortOrder: { sortType: 'NONE', isReverse: false },
        }
      case 'STATS_FETCH_FAILED':
        return { ...state, isLoading: false, isError: true }
      case 'SORT_CHANGED':
        return {
          ...state,
          data: cloneDeep(action.payload.list),
          listBoardStatsPage: cloneDeep(
            action.payload.list.slice(
              (state.selectedPage - 1) * boardStatsNumberOfItemsPerPage,
              state.selectedPage * boardStatsNumberOfItemsPerPage,
            ),
          ),
          sortOrder: action.payload.sort,
        }
      default:
        throw new Error()
    }
  }

  const [listBoardStats, setListBoardStats] = useReducer(boardStatsReducer, {
    initialData: [],
    data: [],
    listBoardStatsPage: [],
    isLoading: false,
    isError: false,
    selectedPage: 1,
    totalItems: 0,
    sortOrder: { sortType: 'NONE', isReverse: false },
    searchInput: '',
  })

  const sailStatsReducer = (state, action) => {
    switch (action.type) {
      case 'NEW_PAGE':
        return {
          ...state,
          listSailStatsPage: cloneDeep(
            state.data.slice(
              (action.payload - 1) * sailStatsNumberOfItemsPerPage,
              action.payload * sailStatsNumberOfItemsPerPage,
            ),
          ),
          selectedPage: action.payload,
        }
      case 'NEW_ITEM_SELECTED':
        return {
          ...state,
          data: cloneDeep(action.payload),
          listSailStatsPage: cloneDeep(
            action.payload.slice(
              (state.selectedPage - 1) * sailStatsNumberOfItemsPerPage,
              state.selectedPage * sailStatsNumberOfItemsPerPage,
            ),
          ),
          totalItems: action.payload.length,
        }
      case 'STATS_FETCH_OK':
        return {
          ...state,
          initialData: cloneDeep(action.payload),
          data: cloneDeep(action.payload),
          listSailStatsPage: cloneDeep(
            action.payload.slice(0, sailStatsNumberOfItemsPerPage),
          ),
          isLoading: false,
          isError: false,
          totalItems: action.payload.length,
          sortOrder: { sortType: 'NONE', isReverse: false },
        }
      case 'STATS_FETCH_FAILED':
        return { ...state, isLoading: false, isError: true }
      case 'SORT_CHANGED':
        return {
          ...state,
          data: cloneDeep(action.payload.list),
          listSailStatsPage: cloneDeep(
            action.payload.list.slice(
              (state.selectedPage - 1) * sailStatsNumberOfItemsPerPage,
              state.selectedPage * sailStatsNumberOfItemsPerPage,
            ),
          ),
          sortOrder: action.payload.sort,
        }
      default:
        throw new Error()
    }
  }

  const [listSailStats, setListSailStats] = useReducer(sailStatsReducer, {
    initialData: [],
    data: [],
    listSailStatsPage: [],
    isLoading: false,
    isError: false,
    selectedPage: 1,
    totalItems: 0,
    sortOrder: { sortType: 'NONE', isReverse: false },
    searchInput: '',
  })

  useEffect(() => {
    if (user.userId === -1) {
      const localUserId = localStorage.getItem('wsurf_userId')
      if (!localUserId || Number(localUserId) === -1) navigateTo('/login')
      else navigateTo('/sessions/')
    }
  }, [])

  useEffect(() => {
    if (firstDiv.current !== null) {
      const topScroll = firstDiv.current.getBoundingClientRect().top
      window.scrollTo({ top: topScroll.current - 120, behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    const userId = user.userId
    const url = '/api/sessionWS/getRigsForUser'
    const data = { userId }

    const dc = decodeURIComponent(document.cookie)
    const sdc = dc.split('=')[1]
    const ws = sdc.split(';')[0]

    fetch(url, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        authorization: 'ws ' + ws,
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        response.json().then((rigs) => {
          let completeRigs = []
          rigs.forEach((data) =>
            data.rigsResult.forEach((rig) => completeRigs.push(rig)),
          )
          const tempBoardStats = cloneDeep(listBoards.initialData)
          tempBoardStats.forEach((board) => {
            let datesList = []
            let nb = 0
            let duration = 0
            const okRigs = sortBy(
              filter(completeRigs, (rig) => rig.boardId === board._id),
              (rig) => new Date(rig.start),
            )

            const firstUse = okRigs.length > 0 ? new Date(okRigs[0].start) : '-'
            const lastUse =
              okRigs.length > 0
                ? new Date(okRigs[okRigs.length - 1].start)
                : '-'

            okRigs.forEach((rig) => {
              const tempDate = format(new Date(rig.start), 'dd-MM-yy')
              const tempOk = find(datesList, (date) => date.date === tempDate)
              if (tempOk === undefined) {
                nb++
                datesList.push({ date: tempDate })
              }
              duration += rig.end - rig.start
            })

            board.board =
              board.brand +
              ' ' +
              board.model +
              ' ' +
              board.volume +
              ' ' +
              board.year
            board.boardId = board._id
            board.nb = nb
            board.duration = duration
            board.firstUse = firstUse
            board.lastUse = lastUse
            board.bg = ''
          })

          setListBoardStats({ type: 'STATS_FETCH_OK', payload: tempBoardStats })

          const tempSailStats = cloneDeep(listSails.initialData)
          tempSailStats.forEach((sail) => {
            let datesList = []
            let nb = 0
            let duration = 0
            const okRigs = filter(
              completeRigs,
              (rig) => rig.sailId === sail._id,
            )
            const firstUse = okRigs.length > 0 ? new Date(okRigs[0].start) : '-'
            const lastUse =
              okRigs.length > 0
                ? new Date(okRigs[okRigs.length - 1].start)
                : '-'
            okRigs.forEach((rig) => {
              const tempDate = format(new Date(rig.start), 'dd-MM-yy')
              const tempOk = find(datesList, (date) => date.date === tempDate)
              if (tempOk === undefined) {
                nb++
                datesList.push({ date: tempDate })
              }
              duration += rig.end - rig.start
            })
            sail.sail =
              sail.brand +
              ' ' +
              sail.model +
              ' ' +
              sail.surface +
              ' ' +
              sail.year
            sail.sailId = sail._id
            sail.nb = nb
            sail.duration = duration
            sail.firstUse = firstUse
            sail.lastUse = lastUse
            sail.bg = ''
          })

          setListSailStats({ type: 'STATS_FETCH_OK', payload: tempSailStats })
        })
      })
      .catch((error) => {
        console.log('Error getting rigs for user', error)
        setListSailStats({ type: 'STATS_FETCH_FAILED' })
      })
  }, [])

  //Sessions handles
  const handleSortBoards = (column_type) => {
    const sort_order =
      column_type === listBoardStats.sortOrder.sortType
        ? !listBoardStats.sortOrder.isReverse
        : true
    let sort_type = column_type
    const sortFunction = SORTS[sort_type]
    const temp = sort_order
      ? sortFunction(Array.from(listBoardStats.data)).reverse()
      : sortFunction(Array.from(listBoardStats.data))
    setListBoardStats({
      type: 'SORT_CHANGED',
      payload: {
        list: temp,
        sort: { sortType: column_type, isReverse: sort_order },
      },
    })
  }

  const handleSortSails = (column_type) => {
    const sort_order =
      column_type === listSailStats.sortOrder.sortType
        ? !listSailStats.sortOrder.isReverse
        : true
    let sort_type = column_type
    const sortFunction = SORTS[sort_type]
    const temp = sort_order
      ? sortFunction(Array.from(listSailStats.data)).reverse()
      : sortFunction(Array.from(listSailStats.data))
    setListSailStats({
      type: 'SORT_CHANGED',
      payload: {
        list: temp,
        sort: { sortType: column_type, isReverse: sort_order },
      },
    })
  }

  const handleSelectNewBoardItem = (item) => {
    const temp = cloneDeep(listBoardStats.data)
    const old_index = temp
      .map(function (e) {
        return e.bg
      })
      .indexOf('selected')
    const new_index = temp
      .map(function (e) {
        return e._id
      })
      .indexOf(item._id)

    if (old_index !== -1) {
      temp[old_index].bg = ''
    }

    if (old_index !== new_index) {
      temp[new_index].bg = 'selected'
    }

    setListBoardStats({ type: 'NEW_ITEM_SELECTED', payload: temp })
  }

  //Pages handles
  const handleClickSelectBoardPage = (num_page) => {
    setListBoardStats({ type: 'NEW_PAGE', payload: parseInt(num_page) })
  }

  const handleClickSelectSailPage = (num_page) => {
    setListSailStats({ type: 'NEW_PAGE', payload: parseInt(num_page) })
  }

  return (
    <div>
      <section className="section" ref={firstDiv}>
        <div className="columns ">
          <div className="column is-9">
            <h1 className="title is-4 has-text-info-dark has-text-weight-semibold">
              {choosenLang.stats}
            </h1>

            <div className="block">
              <div className="tag is-large is-warning">
                {choosenLang.boards}
              </div>
            </div>
            {listBoards.initialData.length > 0 ? (
              <div className="block">
                <>
                  <BoardStatList
                    list={listBoardStats}
                    handleSelectNewItem={handleSelectNewBoardItem}
                    handleSort={handleSortBoards}
                    textClass={textClass}
                  />
                  <PageSelect
                    handleClickSelectPage={handleClickSelectBoardPage}
                    list={listBoardStats}
                    itemsPerPage={boardStatsNumberOfItemsPerPage}
                    buttonsActive={true}
                  />
                </>
              </div>
            ) : (
              listBoards.isLoading === false && (
                <div className="notification is-warning is-light is-notif">
                  {choosenLang.noBoard}
                </div>
              )
            )}
            <div className="block">
              <div className="tag is-large is-primary">{choosenLang.sails}</div>
            </div>
            {listBoards.initialData.length > 0 ? (
              <div className="block">
                <>
                  <SailStatList
                    list={listSailStats}
                    handleSelectNewItem={handleSelectNewBoardItem}
                    handleSort={handleSortSails}
                    textClass={textClass}
                  />
                  <PageSelect
                    handleClickSelectPage={handleClickSelectSailPage}
                    list={listSailStats}
                    itemsPerPage={sailStatsNumberOfItemsPerPage}
                    buttonsActive={true}
                  />
                </>
              </div>
            ) : (
              listSails.isLoading === false && (
                <div className="notification is-primary is-light is-notif">
                  {choosenLang.noSail}
                </div>
              )
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  )
}

export default RigsStats
